import { useState, type FormEvent } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, Send } from "lucide-react";
import { ghostButtonClass, inputClass, primaryButtonClass, textareaClass } from "../components/ui";
import { errorMessage, request } from "../lib/api";
import { getRole } from "../lib/auth";
import type { SupportCategory } from "../support/types";

const teacherCategories: SupportCategory[] = ["access", "pd_day", "content", "technical", "other"];
const facilitatorCategories: SupportCategory[] = [
  "access",
  "pd_day",
  "attendance",
  "technical",
  "other",
];

export function SupportTicketForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const categories = getRole() === "facilitator" ? facilitatorCategories : teacherCategories;
  const [category, setCategory] = useState<SupportCategory>(categories[0]);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const title = subject.trim();
    const details = description.trim();
    if (title.length < 6 || details.length < 10) {
      setError(t("helpFormValidation"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await request<{ id: string }>("/api/support/tickets", {
        method: "POST",
        body: JSON.stringify({
          category,
          subject: title,
          description: details,
        }),
      });
      navigate(`/helpline/tickets/${result.id}`, { replace: true, state: location.state });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-5 pb-8">
      <NavLink to="/helpline" state={location.state} className={ghostButtonClass}>
        <ChevronLeft size={16} />
        {t("navHelpline")}
      </NavLink>
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-app-ink">{t("helpCreateTicket")}</h1>
        <p className="mt-1 text-sm leading-6 text-app-muted">{t("helpFormIntro")}</p>
      </div>
      <form
        onSubmit={(event) => void submit(event)}
        className="flex flex-col gap-5 rounded-xl bg-surface p-4 shadow-raised"
      >
        <label className="grid gap-2 text-sm font-semibold text-app-ink">
          {t("helpCategory")}
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value as SupportCategory)}
            className={inputClass}
          >
            {categories.map((value) => (
              <option value={value} key={value}>
                {t(`helpCategory_${value}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-2 text-sm font-semibold text-app-ink">
          {t("helpSubject")}
          <input
            type="text"
            required
            minLength={6}
            maxLength={120}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder={t("helpSubjectPlaceholder")}
            className={inputClass}
          />
        </label>
        <label className="grid gap-2 text-sm font-semibold text-app-ink">
          {t("helpDescription")}
          <textarea
            required
            minLength={10}
            maxLength={4000}
            rows={6}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t("helpDescriptionPlaceholder")}
            className={textareaClass}
          />
        </label>
        <p className="text-xs leading-5 text-app-muted">{t("helpFormTip")}</p>
        {error && (
          <p role="alert" className="text-sm text-accent-strong">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={saving}
          className={`${primaryButtonClass} flex items-center justify-center gap-2`}
        >
          <Send size={17} />
          {saving ? t("helpSending") : t("helpSendRequest")}
        </button>
      </form>
    </div>
  );
}
